import { styled } from "goober";
import { Children, isValidElement, useId, useState } from "react";
import {
  MapChromeCorner,
  MapChromePill,
  MapChromePressButton,
} from "../MapChrome";
import { Tooltip } from "../Tooltip";

const Container = styled("div")`
  position: relative;
  display: grid;
  min-width: 0;
  overflow: clip;
  border-radius: 12px;

  &:has(:focus-visible) [data-table-controls] {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const ScrollArea = styled("div")`
  grid-area: 1 / 1;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--alpha16) transparent;
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;

  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: -2px;
  }

  & > table th,
  & > table td {
    vertical-align: top;
  }

  &[data-wrap="true"] > table th,
  &[data-wrap="true"] > table td {
    white-space: normal;
    word-break: normal;
    overflow-wrap: anywhere;
  }

  &[data-wrap="true"][data-width="auto"] > table th,
  &[data-wrap="true"][data-width="auto"] > table td {
    max-width: 64ch;
    overflow-wrap: break-word;
  }

  &[data-wrap="true"] > table code,
  &[data-wrap="true"] > table a {
    overflow-wrap: anywhere;
  }

  &[data-wrap="false"] > table th,
  &[data-wrap="false"] > table td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ControlsCorner = styled(MapChromeCorner)`
  grid-area: 1 / 1;
  position: sticky;
  bottom: calc(
    var(--chat-composer-height, 0px) + var(--chat-table-scroll-inset, 0px)
  );
  align-self: end;
  justify-self: end;
  padding: 12px;
`;

const Controls = styled(MapChromePill)`
  @media (hover: hover) {
    &:focus-within:not(:has(:focus-visible)):not([data-visible="true"]) {
      opacity: 0;
      transform: translateY(calc(100% + 12px));
      pointer-events: none;
    }
  }
`;

const Control = styled(MapChromePressButton)`
  & > svg {
    opacity: 0.3;
  }
`;

const WrapControl = styled(Control)`
  & .return-line {
    d: path("M 2 8 L 11.5 8 C 13 8 14 8 14 8 C 14 8 13 8 11.5 8 L 7.5 8");
    transition: d var(--motion-base) var(--ease-out);
  }
  & .last-line {
    d: path("M 2 12 L 14 12");
    transition: d var(--motion-base) var(--ease-out);
  }
  & .arrow {
    opacity: 0;
    transform: translateX(2px);
    transition:
      opacity var(--motion-base) var(--ease-out),
      transform var(--motion-base) var(--ease-out);
  }
  &[data-wrapped="true"] .return-line {
    d: path("M 2 8 L 11.5 8 C 13 8 14 9 14 10 C 14 11 13 12 11.5 12 L 7.5 12");
  }
  &[data-wrapped="true"] .last-line {
    d: path("M 2 12 L 4.5 12");
  }
  &[data-wrapped="true"] .arrow {
    opacity: 1;
    transform: translateX(0);
  }
`;

const WidthControl = styled(Control)`
  & > svg {
    overflow: hidden;
  }
  & rect {
    transition:
      x var(--motion-base) var(--ease-out),
      width var(--motion-base) var(--ease-out);
  }
  &[data-width="fit"] rect {
    width: 2px;
  }
  &[data-width="fit"] .column-first {
    x: 2px;
  }
  &[data-width="fit"] .column-middle {
    x: 7px;
  }
  &[data-width="fit"] .column-last {
    x: 12px;
  }
  &[data-width="wide"] rect {
    width: 8px;
  }
  &[data-width="wide"] .column-first {
    x: -6px;
  }
  &[data-width="wide"] .column-last {
    x: 14px;
  }
`;

const widths = [
  { value: "auto", label: "Auto" },
  { value: "fit", label: "Fit" },
  { value: "wide", label: "Wide" },
] as const;

function columnCount(children: React.ReactNode): number {
  return Children.toArray(children).reduce<number>((count, child) => {
    if (!isValidElement<{ children?: React.ReactNode }>(child)) return count;
    if (child.type === "tr")
      return Math.max(count, Children.count(child.props.children));
    return Math.max(count, columnCount(child.props.children));
  }, 0);
}

export function MarkdownTable({
  children,
  ...props
}: React.TableHTMLAttributes<HTMLTableElement>) {
  const tableId = useId();
  const [hovered, setHovered] = useState(false);
  const [wrap, setWrap] = useState(true);
  const [width, setWidth] = useState<(typeof widths)[number]["value"]>("auto");
  const [wrapPreview, setWrapPreview] = useState(false);
  const [widthPreview, setWidthPreview] = useState(false);
  const widthIndex = widths.findIndex((option) => option.value === width);
  const currentWidth = widths[widthIndex]!;
  const nextWidth = widths[(widthIndex + 1) % widths.length]!;

  return (
    <Container
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ScrollArea
        data-wrap={wrap}
        data-width={width}
        tabIndex={0}
        role="region"
        aria-label="Table"
      >
        <table
          {...props}
          id={tableId}
          style={{
            ...props.style,
            tableLayout: width === "auto" ? "auto" : "fixed",
            width:
              width === "wide"
                ? `max(100%, ${columnCount(children) * 280}px)`
                : "100%",
          }}
        >
          {children}
        </table>
      </ScrollArea>
      <ControlsCorner>
        <Controls
          data-table-controls
          role="group"
          aria-label="Table display options"
          data-visible={hovered ? "true" : undefined}
          data-touch-persistent="true"
        >
          <Tooltip content={wrap ? "Unwrap text" : "Wrap text"}>
            <WrapControl
              aria-label="Wrap text"
              aria-pressed={wrap}
              aria-controls={tableId}
              data-wrapped={wrapPreview ? !wrap : wrap}
              onMouseEnter={() => setWrapPreview(true)}
              onMouseLeave={() => setWrapPreview(false)}
              onPress={() => {
                setWrap(!wrap);
                setWrapPreview(false);
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M2 4h12" />
                <path className="return-line" />
                <path className="last-line" />
                <path className="arrow" d="m9.5 10-2 2 2 2" />
              </svg>
            </WrapControl>
          </Tooltip>
          <Tooltip content={`Column width: ${currentWidth.label}`}>
            <WidthControl
              aria-label={`Column width: ${currentWidth.label}`}
              aria-controls={tableId}
              data-width={widthPreview ? nextWidth.value : width}
              onMouseEnter={() => setWidthPreview(true)}
              onMouseLeave={() => setWidthPreview(false)}
              onPress={() => {
                setWidth(nextWidth.value);
                setWidthPreview(false);
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <rect
                  className="column-first"
                  x="1"
                  y="3"
                  width="1.5"
                  height="10"
                  rx="1"
                />
                <rect
                  className="column-middle"
                  x="4"
                  y="3"
                  width="3"
                  height="10"
                  rx="1"
                />
                <rect
                  className="column-last"
                  x="8.5"
                  y="3"
                  width="6"
                  height="10"
                  rx="1"
                />
              </svg>
            </WidthControl>
          </Tooltip>
        </Controls>
      </ControlsCorner>
    </Container>
  );
}
